import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import {
  EnvelopeIcon,
  ArrowLeftIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { BrandPanel } from './Login';

/* ─── Spinner SVG ─── */
const Spinner: React.FC = () => (
  <svg
    className="animate-spin h-5 w-5 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/* ══════════════════════════════════════
   PAGE FORGOT PASSWORD
══════════════════════════════════════ */
const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await apiService.forgotPassword(email);
      setSubmitted(true);
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Panneau gauche branding ── */}
      <BrandPanel subtitle="Récupérez l'accès à votre espace sécurisé en quelques étapes simples" />

      {/* ── Panneau droit ── */}
      <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-12">

        {/* Logo mobile */}
        <div className="lg:hidden flex flex-col items-center mb-10">
          <div className="w-14 h-14 bg-primary-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg">
            <EnvelopeIcon className="w-7 h-7 text-white" />
          </div>
          <span className="text-lg font-bold text-gray-900">Suivi Démat</span>
          <span className="text-xs text-primary-600 font-semibold tracking-widest uppercase mt-0.5">MTDPCE</span>
        </div>

        <div className="w-full max-w-md">

          {/* Lien retour */}
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 mb-8 transition-colors group"
          >
            <ArrowLeftIcon className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Retour à la connexion
          </Link>

          {/* ── État succès ── */}
          {submitted ? (
            <div className="animate-[fadeIn_0.3s_ease-out]">
              {/* Icône succès animée */}
              <div className="flex flex-col items-center text-center mb-8">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center">
                    <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center">
                      <CheckCircleIcon className="w-9 h-9 text-primary-600" />
                    </div>
                  </div>
                  {/* Cercles animés */}
                  <span className="absolute inset-0 rounded-full border-2 border-primary-300 animate-ping opacity-30" />
                </div>

                <h2 className="text-2xl font-bold text-gray-900">Email envoyé !</h2>
                <p className="mt-3 text-sm text-gray-500 max-w-sm leading-relaxed">
                  Si un compte existe pour{' '}
                  <span className="font-semibold text-gray-700">{email}</span>, vous recevrez
                  un lien de réinitialisation valable{' '}
                  <span className="font-semibold text-gray-700">1 heure</span>.
                </p>
              </div>

              {/* Carte info */}
              <div className="bg-primary-50 border border-primary-100 rounded-xl p-4 mb-8">
                <div className="flex gap-3 items-start">
                  <EnvelopeIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-primary-800">
                    <p className="font-medium mb-0.5">Vérifiez votre boîte mail</p>
                    <p className="text-primary-600/80 text-xs">
                      Pensez à regarder dans vos spams ou courriers indésirables si vous ne trouvez pas l'email.
                    </p>
                  </div>
                </div>
              </div>

              <Link
                to="/login"
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-150 shadow-sm shadow-primary-200"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Retourner à la connexion
              </Link>
            </div>
          ) : (
            /* ── Formulaire ── */
            <>
              <div className="mb-8">
                {/* Icône grande */}
                <div className="w-14 h-14 bg-primary-50 border border-primary-100 rounded-2xl flex items-center justify-center mb-6">
                  <PaperAirplaneIcon className="w-7 h-7 text-primary-600" />
                </div>

                <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                  Mot de passe oublié ?
                </h2>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                  Pas d'inquiétude. Renseignez votre adresse email et nous vous enverrons un lien
                  sécurisé pour réinitialiser votre mot de passe.
                </p>
              </div>

              {/* Erreur */}
              {error && (
                <div
                  className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3.5 rounded-xl text-sm animate-[fadeIn_0.25s_ease-in-out]"
                  role="alert"
                >
                  <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit} noValidate>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Adresse email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all duration-150 text-sm"
                      placeholder="exemple@demat.gov.bf"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 shadow-sm shadow-primary-200"
                >
                  {isLoading ? (
                    <>
                      <Spinner />
                      <span>Envoi en cours…</span>
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-4 w-4" />
                      <span>Envoyer le lien de réinitialisation</span>
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer mobile */}
        <p className="lg:hidden mt-10 text-center text-xs text-gray-400">
          © 2026 MTDPCE — Burkina Faso
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
